import axios, { AxiosResponse, AxiosError } from 'axios';
import { safeAcquireTokenSilent, safeGetAllAccounts, apiRequest } from '../auth/authConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 Minuten für große Excel-Imports
});

// **REQUEST INTERCEPTOR - MSAL TOKEN INTEGRATION**
api.interceptors.request.use(
  async (config) => {
    // **FÜR SUPPLIER PORTAL ROUTES: Verwende supplierPortalSession**
    if (config.url?.includes('/linked-suppliers/') || 
        config.url?.includes('/portal/') ||
        config.url?.includes('/supplier-portal/')) {
      const supplierSession = sessionStorage.getItem('supplierPortalSession');
      if (supplierSession && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${supplierSession}`;
        console.log('🔐 Using supplier portal session token for:', config.url);
      }
    } else {
      // **FÜR ADMIN ROUTES: Verwende MSAL Token**
      if (!config.headers.Authorization && !window.location.pathname.includes('/supplier/')) {
        try {
          const accounts = await safeGetAllAccounts();
          if (accounts.length > 0) {
            const tokenResponse = await safeAcquireTokenSilent();
            
            if (tokenResponse.accessToken) {
              config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
              console.log('🔐 Using MSAL token for admin API call:', config.url);
            }
          }
        } catch (error) {
          console.warn('⚠️ Could not acquire MSAL token, proceeding without auth:', error);
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// **RESPONSE INTERCEPTOR - SESSION ERROR HANDLING**
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // **SUPPLIER PORTAL SESSION ERRORS**
    if (error.response?.status === 401 && 
        (error.config?.url?.includes('/linked-suppliers/') || 
         error.config?.url?.includes('/portal/') ||
         error.config?.url?.includes('/supplier-portal/'))) {
      
      console.log('🔓 Supplier portal session expired');
      
      // Clear supplier session data
      sessionStorage.removeItem('supplierPortalSession');
      sessionStorage.removeItem('supplierPortalSessionTime');
      
      // Dispatch custom event for supplier session error
      window.dispatchEvent(new CustomEvent('supplierSessionError', {
        detail: { status: 401, message: 'Session expired' }
      }));
    }
    
    // **ADMIN SESSION ERRORS - NUR WENN NICHT IN SUPPLIER PORTAL**
    else if (error.response?.status === 401 && !window.location.pathname.includes('/supplier/')) {
      console.log('🔓 MSAL token expired or invalid, triggering re-authentication');
      
      // Dispatch custom event for admin session error - let MSAL handle it
      window.dispatchEvent(new CustomEvent('adminSessionError', {
        detail: { status: 401, message: 'MSAL token expired' }
      }));
      
      // Don't automatically redirect - let the auth context handle this
    }
    
    return Promise.reject(error);
  }
);

// ============================================================================
// AUTHENTICATION
// ============================================================================

export const login = async (credentials: { email: string; password: string }) => {
  const response = await api.post('/auth/login', credentials);
  return { data: response.data };
};

export const register = async (userData: { email: string; password: string; name: string }) => {
  const response = await api.post('/auth/register', userData);
  return { data: response.data };
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  return { data: response.data };
};

export const verifyToken = async () => {
  const response = await api.get('/auth/verify');
  return { data: response.data };
};

// ============================================================================
// QUESTIONNAIRE TEMPLATES
// ============================================================================

export const getQuestionnaireTemplates = async () => {
  const response = await api.get('/templates');
  return { data: response.data };
};

export const getQuestionnaireTemplate = async (id: string) => {
  const response = await api.get(`/templates/${id}`);
  return { data: response.data };
};

export const createQuestionnaireTemplate = async (data: any) => {
  const response = await api.post('/templates', data);
  return { data: response.data };
};

export const updateQuestionnaireTemplate = async (id: string, data: any, changeDescription?: string) => {
  const response = await api.put(`/templates/${id}`, {
    ...data,
    changeDescription,
    updatedBy: 'Current User' // TODO: Get from auth context
  });
  return { data: response.data };
};

export const deleteQuestionnaireTemplate = async (id: string) => {
  const response = await api.delete(`/templates/${id}`);
  return { data: response.data };
};

export const duplicateTemplate = async (id: string) => {
  const response = await api.post(`/templates/${id}/duplicate`, {
    duplicatedBy: 'Current User' // TODO: Get from auth context
  });
  return { data: response.data };
};

// ============================================================================
// TEMPLATE VERSIONING
// ============================================================================

export const getQuestionnaireVersions = async (templateId: string) => {
  const response = await api.get(`/templates/${templateId}/versions`);
  return { data: response.data };
};

export const restoreQuestionnaireVersion = async (currentTemplateId: string, versionId: string) => {
  const response = await api.post(`/templates/${currentTemplateId}/restore`, {
    versionId,
    restoredBy: 'Current User' // TODO: Get from auth context
  });
  return { data: response.data };
};

export const getTemplateVersion = async (templateId: string, versionId: string) => {
  const response = await api.get(`/templates/${templateId}/versions/${versionId}`);
  return { data: response.data };
};

// ============================================================================
// SUPPLIERS
// ============================================================================

export const getSuppliers = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: string;
  importSource?: string;
  sortBy?: string;
  sortOrder?: string;
}) => {
  const response = await api.get('/suppliers', { params });
  return { data: response.data };
};

export const getSupplier = async (id: string) => {
  const response = await api.get(`/suppliers/${id}`);
  return { data: response.data };
};

export const createSupplier = async (data: any) => {
  const response = await api.post('/suppliers', data);
  return { data: response.data };
};

export const updateSupplier = async (id: string, data: any) => {
  const response = await api.put(`/suppliers/${id}`, data);
  return { data: response.data };
};

export const deleteSupplier = async (id: string) => {
  const response = await api.delete(`/suppliers/${id}`);
  return { data: response.data };
};

// ============================================================================
// SUPPLIER IMPORT/EXPORT
// ============================================================================

export const importSuppliers = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/suppliers/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return { data: response.data };
};

export const downloadSupplierTemplate = async (format: 'xlsx' | 'csv') => {
  const response = await api.get(`/suppliers/template/download?format=${format}`, {
    responseType: 'blob'
  });
  return response.data;
};

export const exportSuppliers = async (format: 'xlsx' | 'csv', filters?: any) => {
  const response = await api.get(`/suppliers/export?format=${format}`, {
    params: filters,
    responseType: 'blob'
  });
  return response.data;
};

// ============================================================================
// QUESTIONNAIRE SUBMISSIONS
// ============================================================================

export const getSubmissions = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  templateId?: string;
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
}) => {
  const response = await api.get('/submissions', { params });
  return { data: response.data };
};

export const getSubmission = async (id: string) => {
  const response = await api.get(`/submissions/${id}`);
  return { data: response.data };
};

export const createSubmission = async (data: any) => {
  const response = await api.post('/submissions', data);
  return { data: response.data };
};

export const updateSubmission = async (id: string, data: any) => {
  const response = await api.put(`/submissions/${id}`, data);
  return { data: response.data };
};

export const deleteSubmission = async (id: string) => {
  const response = await api.delete(`/submissions/${id}`);
  return { data: response.data };
};

export const sendQuestionnaireToSupplier = async (data: {
  supplierId: string;
  questionnaireTemplateId: string;
  language?: string;
  dueDate?: string;
  notes?: string;
}) => {
  const response = await api.post('/submissions/send', data);
  return { data: response.data };
};

export const submitQuestionnaire = async (submissionId: string, answers: any) => {
  const response = await api.post(`/submissions/${submissionId}/submit`, { answers });
  return { data: response.data };
};

export const approveSubmission = async (id: string, notes?: string) => {
  const response = await api.put(`/submissions/${id}/approve`, { notes });
  return { data: response.data };
};

export const rejectSubmission = async (id: string, reason: string) => {
  const response = await api.put(`/submissions/${id}/reject`, { reason });
  return { data: response.data };
};

// ============================================================================
// FILE UPLOADS
// ============================================================================

export const uploadFile = async (file: File, category?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (category) formData.append('category', category);
  
  const response = await api.post('/uploads', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return { data: response.data };
};

export const deleteFile = async (fileId: string) => {
  const response = await api.delete(`/uploads/${fileId}`);
  return { data: response.data };
};

export const getFileInfo = async (fileId: string) => {
  const response = await api.get(`/uploads/${fileId}`);
  return { data: response.data };
};

// ============================================================================
// EMAIL & NOTIFICATIONS
// ============================================================================

export const sendEmail = async (data: {
  to: string[];
  subject: string;
  template: string;
  data: any;
}) => {
  const response = await api.post('/email/send', data);
  return { data: response.data };
};

export const sendQuestionnaireEmail = async (data: any) => {
  const response = await api.post('/email/questionnaire', data);
  return { data: response.data };
};

export const sendReminderEmail = async (submissionId: string) => {
  const response = await api.post(`/email/reminder/${submissionId}`);
  return { data: response.data };
};

// ============================================================================
// REPORTS & ANALYTICS
// ============================================================================

export const getDashboardStats = async () => {
  const response = await api.get('/reports/dashboard');
  return { data: response.data };
};

export const getSubmissionStats = async (filters?: any) => {
  const response = await api.get('/reports/submissions', { params: filters });
  return { data: response.data };
};

export const getSupplierStats = async (filters?: any) => {
  const response = await api.get('/reports/suppliers', { params: filters });
  return { data: response.data };
};

export const exportSubmissions = async (format: 'xlsx' | 'csv' | 'pdf', filters?: any) => {
  const response = await api.get(`/reports/submissions/export?format=${format}`, {
    params: filters,
    responseType: 'blob'
  });
  return response.data;
};

// ============================================================================
// SYSTEM & SETTINGS
// ============================================================================

export const getSystemSettings = async () => {
  const response = await api.get('/settings');
  return { data: response.data };
};

export const updateSystemSettings = async (settings: any) => {
  const response = await api.put('/settings', settings);
  return { data: response.data };
};

export const getAuditLog = async (params?: any) => {
  const response = await api.get('/audit', { params });
  return { data: response.data };
};

// ============================================================================
// PREDEFINED TEMPLATES
// ============================================================================

export const getPredefinedTemplates = async () => {
  const response = await api.get('/templates/predefined');
  return { data: response.data };
};

export const createFromPredefined = async (templateId: string, customizations?: any) => {
  const response = await api.post(`/templates/predefined/${templateId}/create`, customizations);
  return { data: response.data };
};

// ============================================================================
// ADMIN FILE UPLOAD
// ============================================================================

export const uploadAdminFiles = async (files: FileList): Promise<any> => {
  const formData = new FormData();
  
  Array.from(files).forEach(file => {
    formData.append('files', file);
  });

  const response = await api.post('/questionnaires/admin-files', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return { data: response.data };
};

// ============================================================================
// LEGACY COMPATIBILITY (Deprecated - will be removed)
// ============================================================================

// **@deprecated Use getQuestionnaireTemplates instead**
export const getQuestionnaires = getQuestionnaireTemplates;

// **@deprecated Use getQuestionnaireTemplate instead**
export const getQuestionnaireById = getQuestionnaireTemplate;
export const getQuestionnaire = getQuestionnaireTemplate;

// **@deprecated Use createQuestionnaireTemplate instead**
export const createQuestionnaire = createQuestionnaireTemplate;

// **@deprecated Use updateQuestionnaireTemplate instead**
export const updateQuestionnaire = updateQuestionnaireTemplate;

// **@deprecated Use deleteQuestionnaireTemplate instead**
export const deleteQuestionnaire = deleteQuestionnaireTemplate;

// **@deprecated Use getQuestionnaireTemplates instead**
export const getFormVersions = getQuestionnaireTemplates;

// **@deprecated Use deleteQuestionnaireTemplate instead**
export const deleteFormVersion = deleteQuestionnaireTemplate;

// ============================================================================
// API HEALTH & UTILITIES
// ============================================================================

export const checkApiHealth = async () => {
  const response = await api.get('/health');
  return { data: response.data };
};

export const getApiVersion = async () => {
  const response = await api.get('/version');
  return { data: response.data };
};

// ============================================================================
// FUTURE: BC INTERFACE (Prepared for Business Central integration)
// ============================================================================

export const getBCSuppliers = async (params?: any) => {
  const response = await api.get('/bc/suppliers', { params });
  return { data: response.data };
};

export const syncWithBC = async (entity: 'suppliers' | 'items', options?: any) => {
  const response = await api.post(`/bc/sync/${entity}`, options);
  return { data: response.data };
};

export const getBCSyncStatus = async () => {
  const response = await api.get('/bc/sync/status');
  return { data: response.data };
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default api;

// ============================================================================
// TYPE DEFINITIONS (Optional - for better TypeScript support)
// ============================================================================

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status?: number;
}

export interface PaginatedResponse<T = any> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SupplierFilters {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: string;
  importSource?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface SubmissionFilters {
  page?: number;
  limit?: number;
  status?: string;
  templateId?: string;
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
}